import { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Loader2, Pencil, Check, X, Undo2 } from 'lucide-react';
import { formatCurrency } from '@/lib/budget-utils';
import {
  fetchBillingInvoiceById,
  updateDraftDescription,
  mapInvoiceStatus,
  type BillingInvoice,
  type BillingInvoiceLineItem,
} from '@/lib/data-service';

interface InvoiceDetailsSheetProps {
  invoiceId: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleteDraft?: (invoiceId: number) => Promise<void>;
  onReturnToDraft?: () => void;
}

export function InvoiceDetailsSheet({
  invoiceId,
  open,
  onOpenChange,
  onDeleteDraft,
  onReturnToDraft,
}: InvoiceDetailsSheetProps) {
  const [invoice, setInvoice] = useState<BillingInvoice | null>(null);
  const [lineItems, setLineItems] = useState<BillingInvoiceLineItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editDescription, setEditDescription] = useState('');
  const [isSavingDescription, setIsSavingDescription] = useState(false);

  // Fetch invoice details when opened
  useEffect(() => {
    if (!open || !invoiceId) {
      setInvoice(null);
      setLineItems([]);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const data = await fetchBillingInvoiceById(invoiceId);
        setInvoice(data.invoice);
        setLineItems(data.lineItems || []);
      } catch (error) {
        console.error('Failed to fetch invoice details:', error);
        toast.error('Failed to load invoice details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [open, invoiceId]);

  const getStatusBadge = (status: number) => {
    const statusText = mapInvoiceStatus(status);
    switch (statusText) {
      case 'Paid':
        return <Badge variant="success">{statusText}</Badge>;
      case 'Sent':
        return <Badge variant="default">{statusText}</Badge>;
      case 'Overdue':
        return <Badge variant="destructive">{statusText}</Badge>;
      case 'Draft':
        return <Badge variant="secondary">{statusText}</Badge>;
      case 'Void':
        return <Badge variant="outline">{statusText}</Badge>;
      default:
        return <Badge variant="outline">{statusText}</Badge>;
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const handleReturnToDraft = async () => {
    if (!invoice || !onDeleteDraft) return;

    setIsDeleting(true);
    try {
      await onDeleteDraft(invoice.id);
      toast.success('Invoice deleted and WIP items returned to unbilled');
      setDeleteDialogOpen(false);
      onOpenChange(false);
      onReturnToDraft?.();
    } catch (error) {
      console.error('Failed to return to draft:', error);
      toast.error('Failed to return invoice to draft WIP');
    } finally {
      setIsDeleting(false);
    }
  };

  const isDraft = invoice?.status === 0;

  const handleEditDescription = () => {
    setEditDescription(invoice?.description || '');
    setIsEditingDescription(true);
  };

  const handleCancelEditDescription = () => {
    setIsEditingDescription(false);
    setEditDescription('');
  };

  const handleSaveDescription = async () => {
    if (!invoice) return;
    setIsSavingDescription(true);
    try {
      await updateDraftDescription(invoice.id, editDescription || null);
      setInvoice({ ...invoice, description: editDescription || undefined });
      setIsEditingDescription(false);
      toast.success('Description updated');
    } catch (error) {
      console.error('Failed to update description:', error);
      toast.error('Failed to update description');
    } finally {
      setIsSavingDescription(false);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-2xl p-0 flex flex-col h-full">
          {/* Fixed Header */}
          <div className="border-b bg-background p-6 pb-4 flex-shrink-0">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1 flex-1">
                <SheetTitle className="text-2xl font-bold">
                  {invoice?.invoiceNumber || `Invoice #${invoiceId}`}
                </SheetTitle>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  {invoice && getStatusBadge(invoice.status)}
                  {invoice?.invoiceDate && (
                    <>
                      <span>-</span>
                      <span>{formatDate(invoice.invoiceDate)}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 min-h-0 overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : invoice ? (
                <div className="p-6 space-y-6">
                  {/* Invoice Details */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Invoice Details</h3>
                    <div className="grid gap-3">
                      <div className="grid grid-cols-3 gap-2">
                        <span className="text-sm text-muted-foreground">Invoice Number</span>
                        <span className="col-span-2 text-sm font-medium">
                          {invoice.invoiceNumber || '-'}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <span className="text-sm text-muted-foreground">Status</span>
                        <span className="col-span-2 text-sm">
                          {getStatusBadge(invoice.status)}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <span className="text-sm text-muted-foreground">Invoice Date</span>
                        <span className="col-span-2 text-sm">
                          {formatDate(invoice.invoiceDate)}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <span className="text-sm text-muted-foreground">Due Date</span>
                        <span className="col-span-2 text-sm">
                          {formatDate(invoice.dueDate)}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <span className="text-sm text-muted-foreground">Description</span>
                        <div className="col-span-2">
                          {isEditingDescription ? (
                            <div className="flex items-center gap-1">
                              <Input
                                value={editDescription}
                                onChange={(e) => setEditDescription(e.target.value)}
                                className="h-7 text-sm"
                                placeholder="Enter description..."
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveDescription();
                                  if (e.key === 'Escape') handleCancelEditDescription();
                                }}
                                disabled={isSavingDescription}
                              />
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 shrink-0"
                                onClick={handleSaveDescription}
                                disabled={isSavingDescription}
                              >
                                {isSavingDescription ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Check className="h-3.5 w-3.5" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 shrink-0"
                                onClick={handleCancelEditDescription}
                                disabled={isSavingDescription}
                              >
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              <span className="text-sm">{invoice.description || '-'}</span>
                              {isDraft && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 shrink-0"
                                  onClick={handleEditDescription}
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      {invoice.notes && (
                        <div className="grid grid-cols-3 gap-2">
                          <span className="text-sm text-muted-foreground">Notes</span>
                          <span className="col-span-2 text-sm">{invoice.notes}</span>
                        </div>
                      )}
                      {invoice.accessFinancialsId && (
                        <div className="grid grid-cols-3 gap-2">
                          <span className="text-sm text-muted-foreground">External ID</span>
                          <span className="col-span-2 text-sm font-mono">
                            {invoice.accessFinancialsId}
                          </span>
                        </div>
                      )}
                      {invoice.sentDate && (
                        <div className="grid grid-cols-3 gap-2">
                          <span className="text-sm text-muted-foreground">Sent Date</span>
                          <span className="col-span-2 text-sm">
                            {formatDate(invoice.sentDate)}
                          </span>
                        </div>
                      )}
                      {invoice.paidDate && (
                        <div className="grid grid-cols-3 gap-2">
                          <span className="text-sm text-muted-foreground">Paid Date</span>
                          <span className="col-span-2 text-sm">
                            {formatDate(invoice.paidDate)}
                          </span>
                        </div>
                      )}
                      {invoice.voidDate && (
                        <div className="grid grid-cols-3 gap-2">
                          <span className="text-sm text-muted-foreground">Void Date</span>
                          <span className="col-span-2 text-sm">
                            {formatDate(invoice.voidDate)}
                          </span>
                        </div>
                      )}
                      {invoice.voidReason && (
                        <div className="grid grid-cols-3 gap-2">
                          <span className="text-sm text-muted-foreground">Void Reason</span>
                          <span className="col-span-2 text-sm">{invoice.voidReason}</span>
                        </div>
                      )}
                      <div className="grid grid-cols-3 gap-2">
                        <span className="text-sm text-muted-foreground">Created By</span>
                        <span className="col-span-2 text-sm">
                          {invoice.createdBy || '-'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Financial Summary */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Financial Summary</h3>
                    <div className="grid gap-3 md:grid-cols-3">
                      <div className="space-y-1 p-3 border rounded-lg">
                        <div className="text-sm text-muted-foreground">Subtotal</div>
                        <div className="text-lg font-bold">{formatCurrency(invoice.subtotal)}</div>
                      </div>
                      <div className="space-y-1 p-3 border rounded-lg">
                        <div className="text-sm text-muted-foreground">Tax</div>
                        <div className="text-lg font-bold">{formatCurrency(invoice.taxAmount)}</div>
                      </div>
                      <div className="space-y-1 p-3 border rounded-lg">
                        <div className="text-sm text-muted-foreground">Gross Total</div>
                        <div className="text-lg font-bold">{formatCurrency(invoice.grossTotal)}</div>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Line Items */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold">Line Items</h3>
                      <span className="text-sm text-muted-foreground">
                        {lineItems.length} item{lineItems.length !== 1 ? 's' : ''}
                      </span>
                    </div>

                    {lineItems.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <p className="text-sm">No line items</p>
                      </div>
                    ) : (
                      <div className="rounded-md border">
                        <ScrollArea className="w-full">
                          <ScrollAreaViewport className="w-full">
                            <div className="min-w-max">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead className="w-[40px]">#</TableHead>
                                    <TableHead className="min-w-[200px]">Description</TableHead>
                                    <TableHead className="text-right w-[80px]">Qty</TableHead>
                                    <TableHead className="text-right w-[100px]">Rate</TableHead>
                                    <TableHead className="text-right w-[100px]">Net</TableHead>
                                    <TableHead className="text-right w-[100px]">Tax</TableHead>
                                    <TableHead className="text-right w-[100px]">Gross</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {lineItems.map((item, index) => (
                                    <TableRow key={item.id}>
                                      <TableCell className="text-muted-foreground">
                                        {index + 1}
                                      </TableCell>
                                      <TableCell className="font-medium">
                                        {item.description || '-'}
                                      </TableCell>
                                      <TableCell className="text-right">
                                        {item.quantity.toFixed(2)}
                                      </TableCell>
                                      <TableCell className="text-right">
                                        {formatCurrency(item.rate)}
                                      </TableCell>
                                      <TableCell className="text-right">
                                        {formatCurrency(item.netAmount)}
                                      </TableCell>
                                      <TableCell className="text-right">
                                        {formatCurrency(item.taxAmount)}
                                      </TableCell>
                                      <TableCell className="text-right font-medium">
                                        {formatCurrency(item.grossAmount)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                  {/* Totals row */}
                                  <TableRow className="bg-muted/50 font-medium">
                                    <TableCell colSpan={4} className="text-right">
                                      Totals
                                    </TableCell>
                                    <TableCell className="text-right">
                                      {formatCurrency(invoice.subtotal)}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      {formatCurrency(invoice.taxAmount)}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      {formatCurrency(invoice.grossTotal)}
                                    </TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </div>
                          </ScrollAreaViewport>
                          <ScrollAreaScrollbar orientation="horizontal" />
                        </ScrollArea>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center py-20 text-muted-foreground">
                  <p className="text-sm">No invoice data available</p>
                </div>
              )}
          </div>

          {/* Sticky Footer */}
          {invoice && isDraft && (
            <div className="border-t bg-background p-4 flex-shrink-0">
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="destructive"
                  onClick={() => setDeleteDialogOpen(true)}
                  disabled={isDeleting}
                >
                  <Undo2 className="h-4 w-4 mr-2" />
                  Return to Draft WIP
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Return to Draft WIP?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete the draft invoice and return all {lineItems.length} WIP item{lineItems.length !== 1 ? 's' : ''} back to unbilled status. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReturnToDraft}
              disabled={isDeleting}
              className="bg-error-600 hover:bg-error-700 focus:ring-error-600"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Returning...
                </>
              ) : (
                'Return to Draft WIP'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
